import Cookies from 'js-cookie';
import React, { Dispatch, useCallback, useReducer } from 'react';
import { useNavigate, useLocation, matchRoutes, RouteMatch, useResolvedPath } from 'react-router-dom';
import { getAppointment, IAppointment } from '../API/GoMoto';
import { ServiceCategory } from '../API/Enums';
import { KioskCookies } from '../helpers/Cookies';
import { AppointmentDispatchTypes, useAppointmentDispatch, useAppointmentState, useAppointmentStateUnsafe, useAppointmentUpdateAndPersist } from './Appointment/appointment';
import { Types, useCartDispatch } from './cart';
import { AppRoutePaths } from '../helpers/Routes';
import { useLocalizationDispatch } from './Localization/localization';
import { LocalizationDispatchTypes } from './Localization/localization.types';
import { ActionMap } from '../API/GoMoto.types';

interface ISavedSessionCookieData {
    appointmentID?: number;
    appointmentKey: string;
    route: string;
}

interface ISavedSessionState extends ISavedSessionCookieData {
    showSaveButton?: boolean;
}

const initialState: ISavedSessionCookieData = {
    appointmentID: 0,
    appointmentKey: '',
    route: '',
};

type SavedSessionProviderProps = {
    children: React.ReactNode;
};

const SavedSessionStateContext = React.createContext<ISavedSessionState>(initialState);
const SavedSessionDispatchContext = React.createContext<Dispatch<SavedSessionActions>>(() => null);

export enum SavedSessionDispatchTypes {
    Update = 'SAVED_SESSION_UPDATE',
    ShowSaveButton = 'SHOW_SAVE_BUTTON'
}

/** Creates an appointment context */
export function SavedSessionProvider({ children }: SavedSessionProviderProps) {
    const location = useLocation();
    const { pathname } = useResolvedPath('.');
    const appointment = useAppointmentStateUnsafe();
    const [state, dispatch] = useReducer(savedSessionReducer, initialState);
    const enableSaveSessionRoutes: RouteMatch[] | null = matchRoutes(
        [
            {
                path: `${pathname}appointment/services-previously-added`,
            },
            {
                path: `${pathname}appointment/recalls`,
            },
            {
                path: `${pathname}appointment/valuation`,
            },
            {
                path: `${pathname}appointment/hunter`,
            },
            {
                path: `${pathname}appointment/additional-services`,
            },
            {
                path: `${pathname}appointment/select-accessories`,
            },
            {
                path: `${pathname}appointment/xtime`,
            },
            {
                path: `${pathname}appointment/services`,
            },
            {
                path: `${pathname}appointment/services/add`,
            },
            {
                path: `${pathname}appointment/services/comments`,
            },
            {
                path: `${pathname}appointment/transportation`,
            },
            {
                path: `${pathname}appointment/transportation/pickup`,
            },
            {
                path: `${pathname}appointment/summary`,
            },
        ],
        location,
    );
    const enableSaveSession: boolean = !!enableSaveSessionRoutes && enableSaveSessionRoutes.length > 0;

    return (
        <SavedSessionStateContext.Provider value={{ ...state,
            showSaveButton: !!enableSaveSession && !!appointment?.key }}
        >
            <SavedSessionDispatchContext.Provider value={dispatch}>{children}</SavedSessionDispatchContext.Provider>
        </SavedSessionStateContext.Provider>
    );
}

/** Retrieves the saved appointment from the closest context */
export function useSavedSessionState() {
    const context = React.useContext(SavedSessionStateContext);
    if (context === undefined) {
        throw new Error('useSavedSessionState must be used within a AppointmentProvider');
    }

    return context;
}

/** Returns a dispatch function to update the appointment */
export function useSavedSessionDispatch() {
    const context = React.useContext(SavedSessionDispatchContext);
    if (context === undefined) {
        throw new Error('useSavedSessionDispatch must be used within a SavedSessionDispatchProvider');
    }

    return context;
}

/** Navigates to the save session page with the current route */
export function useSavedSessionTrySave() {
    const navigate = useNavigate();
    const location = useLocation();

    return async () => {
        navigate(AppRoutePaths.SaveSession, { state: { from: location.pathname } });
    };
}

/** Updates and saves the appointment */
export function useSavedSessionSave() {
    const appointment = useAppointmentState();
    const savedSessionDispatch = useSavedSessionDispatch();
    const location = useLocation();
    const setAppointment = useAppointmentUpdateAndPersist();

    return async () => {
        if (location.state) {
            const { from } = (location.state as any) || { from: { pathname: '/' } };
            const data: ISavedSessionCookieData = { appointmentKey: appointment.key,
                route: from };
            Cookies.set(KioskCookies.SavedSession, JSON.stringify(data));
            savedSessionDispatch({ type: SavedSessionDispatchTypes.Update,
                payload: data });
            await setAppointment(
                {
                    ...appointment,
                    mobileCheckInUrl: from,
                },
                false,
                false,
                false,
                true,
                undefined,
                true,
                false,
                true,
            );
        }
    };
}

/** Clears the session and appointment */
export function useSavedSessionClear() {
    const appointmentDispatch = useAppointmentDispatch();
    const navigate = useNavigate();

    return async () => {
        appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
        Cookies.remove(KioskCookies.SavedSession);
        navigate(AppRoutePaths.Appointment);
    };
}

/** Checks if a cookie exists to load session */
export const hasSavedSession = () => {
    const cookie = Cookies.get(KioskCookies.SavedSession);
    if (cookie) {
        const data: ISavedSessionCookieData = JSON.parse(cookie);

        return !!data;
    }

    return false;
};

/** Loads and sets appointment from cookie */
export function useSavedSessionLoad() {
    const setAppointment = useAppointmentUpdateAndPersist();
    const navigate = useNavigate();
    const cartDispatch = useCartDispatch();
    const localizationDispatch = useLocalizationDispatch();

    return async () => {
        const cookie = Cookies.get(KioskCookies.SavedSession);

        if (cookie) {
            const data: ISavedSessionCookieData = JSON.parse(cookie);

            if (data) {
                setAppointment(await getAppointment(data.appointmentKey))
                    .then((appointment: IAppointment | null) => {
                        if (appointment) {
                            appointment.servicesConfirmed?.forEach((s) => {
                                const fullService = {
                                    id: s.id,
                                    name: s.name || '',
                                    nameSpanish: s.nameAltLang || '',
                                    description: s.description || '',
                                    descriptionSpanish: s.descriptionAltLang || '',
                                    price: s.price,
                                    priceLow: s.priceLow,
                                    priceHigh: s.priceHigh,
                                    spgPricing: s.spgPricing,
                                    category: ServiceCategory.All,
                                    imagePath: '',
                                    imageUrl: '',
                                    isTransport: s.isTransport,
                                    useSpgPricing: !!s.spgPricing,
                                    comment: s.comment,
                                };
                                cartDispatch({ type: Types.AddService,
                                    payload: fullService });
                            });

                            localizationDispatch({ type: LocalizationDispatchTypes.SetLocale,
                                payload: appointment.locale });

                            appointment.xtimeServices?.forEach((s) => {
                                cartDispatch({
                                    type: Types.AddXtimeService,
                                    payload: {
                                        id: s.id,
                                        goMotoId: s.goMotoId,
                                        type: 'standard',
                                        name: s.name,
                                        opCode: s.opCode,
                                        price: s.price,
                                        description: s.description,
                                        isCommonRepair: s.isCommonRepair,
                                        comment: s.comment,
                                    },
                                });
                            });

                            appointment.maintenanceMenusConfirmed?.forEach((m) => {
                                cartDispatch({ type: Types.AddMaintMenu,
                                    payload: m });
                            });

                            appointment.rndServices?.forEach((rnd) => {
                                cartDispatch({ type: Types.AddRNDService,
                                    payload: rnd });
                            });

                            appointment.comment && cartDispatch({ type: Types.AddComment,
                                payload: { key: 'Services',
                                    comment: appointment.comment } });
                            appointment.transportation &&
                            cartDispatch({
                                type: Types.SetTransportation,
                                payload: {
                                    type: appointment.transportation.type,
                                    subtype: appointment.transportation.subtype,
                                },
                            });
                            navigate(data.route);
                        }
                    });

                return;
            }
        }

        Cookies.remove(KioskCookies.SavedSession);
    };
}

export function useSavedSessionFinish() {
    return useCallback(async () => {
        Cookies.remove(KioskCookies.SavedSession);
    }, []);
}

type SavedSessionPayload = {
    [SavedSessionDispatchTypes.Update]: ISavedSessionCookieData;
    [SavedSessionDispatchTypes.ShowSaveButton]: boolean;
};

export type SavedSessionActions = ActionMap<SavedSessionPayload>[keyof ActionMap<SavedSessionPayload>];

export const savedSessionReducer = (state: ISavedSessionState, action: SavedSessionActions) => {
    switch (action.type) {
        case SavedSessionDispatchTypes.Update:
            return { ...state,
                ...action.payload };
        case SavedSessionDispatchTypes.ShowSaveButton:
            return { ...state,
                showSaveButton: action.payload };
        default:
            return state;
    }
};
